import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/fetchCaseApi";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Field, SelectShell, inputStyles, selectStyles } from "../../components/crm/formControls";
import toast from "react-hot-toast";

import SelectMenu from "../../components/crm/SelectMenu";
// Top-level categories shown in the High Court search page.
const SEARCH_CATEGORIES = [
  { id: "case-status", label: "Case Status" },
  { id: "court-orders", label: "Court Orders" },
];

const SEARCH_TABS = [
  { id: "party-name", label: "Party Name" },
  { id: "case-number", label: "Case Number" },
  { id: "filing-number", label: "Filing Number" },
  { id: "advocate-name", label: "Advocate Name" },
  { id: "cnr-number", label: "CNR Number" },
  { id: "case-type", label: "Case Type" },
];

// Court Orders sub-searches — same form style as Case Status.
const COURT_ORDER_TABS = [
  { id: "co-party-name", label: "Party Name" },
  { id: "co-case-number", label: "Case Number" },
  { id: "co-order-date", label: "Order Date" },
];

// Convert a native date input value (YYYY-MM-DD) to the portal's DD-MM-YYYY.
const toDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

const HIGH_COURTS = [
  { code: "13", name: "Allahabad High Court" },
  { code: "1", name: "Bombay High Court" },
  { code: "16", name: "Calcutta High Court" },
  { code: "6", name: "Gauhati High Court" },
  { code: "29", name: "High Court for State of Telangana" },
  { code: "2", name: "High Court of Andhra Pradesh" },
  { code: "18", name: "High Court of Chhattisgarh" },
  { code: "26", name: "High Court of Delhi" },
  { code: "17", name: "High Court of Gujarat" },
  { code: "5", name: "High Court of Himachal Pradesh" },
  { code: "12", name: "High Court of Jammu and Kashmir" },
  { code: "7", name: "High Court of Jharkhand" },
  { code: "3", name: "High Court of Karnataka" },
  { code: "4", name: "High Court of Kerala" },
  { code: "23", name: "High Court of Madhya Pradesh" },
  { code: "25", name: "High Court of Manipur" },
  { code: "21", name: "High Court of Meghalaya" },
  { code: "11", name: "High Court of Orissa" },
  { code: "22", name: "High Court of Punjab and Haryana" },
  { code: "9", name: "High Court of Rajasthan" },
  { code: "24", name: "High Court of Sikkim" },
  { code: "20", name: "High Court of Tripura" },
  { code: "15", name: "High Court of Uttarakhand" },
  { code: "10", name: "Madras High Court" },
  { code: "8", name: "Patna High Court" },
];

const SEARCH_TIPS = [
  "Use Party Name for better results",
  "Enter minimum 3 characters",
  "Captcha is case sensitive",
];

function Cases() {
  const navigate = useNavigate();

  // Court & bench state
  const [courtCode, setCourtCode] = useState("");
  const [selectedBench, setSelectedBench] = useState("");
  const [benches, setBenches] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);

  // Captcha & session state
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [cookie, setCookie] = useState(null);

  // UI state
  const [searchCategory, setSearchCategory] = useState("case-status");
  const [activeTab, setActiveTab] = useState("party-name");
  const [loading, setLoading] = useState(false);
  const [benchLoading, setBenchLoading] = useState(false);
  const [error, setError] = useState("");

  // Year options
  const yearOptions = Array.from({ length: 76 }, (_, i) => String(new Date().getFullYear() - i));

  // Separate form state per tab
  const [partyForm, setPartyForm] = useState({
    petitionerName: "",
    registrationYear: new Date().getFullYear().toString(),
    status: "Pending",
    captcha: "",
  });
  const [caseNumberForm, setCaseNumberForm] = useState({
    caseType: "",
    caseNumber: "",
    registrationYear: new Date().getFullYear().toString(),
    captcha: "",
  });
  const [filingForm, setFilingForm] = useState({
    filingNumber: "",
    year: new Date().getFullYear().toString(),
    captcha: "",
  });
  const [advocateForm, setAdvocateForm] = useState({
    advocateName: "",
    status: "Pending",
    captcha: "",
  });
  const [cnrForm, setCnrForm] = useState({
    cino: "",
    captcha: "",
  });
  const [caseTypeForm, setCaseTypeForm] = useState({
    caseType: "",
    year: new Date().getFullYear().toString(),
    status: "Pending",
    captcha: "",
  });

  // Court Orders form state (one per sub-tab)
  const [coPartyForm, setCoPartyForm] = useState({
    partyName: "",
    registrationYear: new Date().getFullYear().toString(),
    captcha: "",
  });
  const [coCaseNumberForm, setCoCaseNumberForm] = useState({
    caseType: "",
    caseNumber: "",
    registrationYear: new Date().getFullYear().toString(),
    captcha: "",
  });
  const [coOrderDateForm, setCoOrderDateForm] = useState({
    fromDate: "",
    toDate: "",
    captcha: "",
  });

  // Clear localStorage on mount
  useEffect(() => {
    localStorage.removeItem("hc_search_payload");
    localStorage.removeItem("hc_case_details_payload");
  }, []);

  // Fetch benches when courtCode changes
  useEffect(() => {
    if (courtCode) {
      fetchBenches(courtCode);
    }
  }, [courtCode]);

  // Fetch captcha when selectedBench changes
  useEffect(() => {
    if (selectedBench) {
      fetchCaptcha();
    }
  }, [selectedBench]);

  // Fetch case types when relevant tab is active and cookies are available
  useEffect(() => {
    if (
      (activeTab === "case-number" ||
        activeTab === "case-type" ||
        activeTab === "co-case-number") &&
      selectedBench &&
      cookie
    ) {
      fetchCaseTypes();
    }
  }, [activeTab, selectedBench, cookie]);

  // Clear captcha input on tab change and refresh captcha
  useEffect(() => {
    if (selectedBench) {
      fetchCaptcha();
    }
    setError("");
    // Clear captcha for the active tab
    switch (activeTab) {
      case "party-name":
        setPartyForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "case-number":
        setCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "filing-number":
        setFilingForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "advocate-name":
        setAdvocateForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "cnr-number":
        setCnrForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "case-type":
        setCaseTypeForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "co-party-name":
        setCoPartyForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "co-case-number":
        setCoCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "co-order-date":
        setCoOrderDateForm((prev) => ({ ...prev, captcha: "" }));
        break;
      default:
        break;
    }
  }, [activeTab]);

  // Switch between Case Status and Court Orders categories.
  const handleCategoryChange = (category) => {
    if (category === searchCategory) return;
    setSearchCategory(category);
    setError("");
    setActiveTab(category === "court-orders" ? "co-party-name" : "party-name");
  };

  // API calls
  const fetchBenches = async (courtId) => {
    setBenchLoading(true);
    setError("");
    try {
      const data = await apiRequest("fetchBenches", { state_code: courtId });
      if (data.benches) {
        setBenches(data.benches);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch benches");
    } finally {
      setBenchLoading(false);
    }
  };

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    setError("");
    try {
      const data = await apiRequest("fetchCaptcha", { selectedBench }, cookie);
      if (data.captchaImageBase64) {
        setCaptchaImage(data.captchaImageBase64);
        setCookie(data.cookies);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch captcha");
    } finally {
      setCaptchaLoading(false);
    }
  };

  const fetchCaseTypes = async () => {
    try {
      const data = await apiRequest(
        "case/types",
        {
          court_code: selectedBench,
          state_code: courtCode,
          cookies: cookie,
        },
        cookie
      );
      if (data.caseTypes) {
        setCaseTypes(data.caseTypes);
      }
    } catch (err) {
      console.warn("Failed to fetch case types:", err);
    }
  };

  // Court/bench handlers
  const handleCourtChange = (e) => {
    setCourtCode(e.target.value);
    setSelectedBench("");
    setBenches([]);
    setCaptchaImage("");
    setCookie(null);
    setCaseTypes([]);
  };

  const handleBenchChange = (e) => {
    setSelectedBench(e.target.value);
  };

  // Search handler
  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let requestData;
      let endpoint;

      switch (activeTab) {
        case "party-name":
          requestData = {
            captcha: partyForm.captcha,
            petres_name: partyForm.petitionerName,
            rgyear: partyForm.registrationYear,
            caseStatusSearchType: "CSPartyName",
            f: partyForm.status,
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            sessionId: cookie?.JSESSION || cookie?.JSESSIONID || "",
            cookies: cookie,
          };
          endpoint = "api/case";
          break;

        case "case-number":
          requestData = {
            captcha: caseNumberForm.captcha,
            case_type: caseNumberForm.caseType,
            case_no: caseNumberForm.caseNumber,
            rgyear: caseNumberForm.registrationYear,
            caseStatusSearchType: "CScaseNumber",
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            caseNoType: "new",
            displayOldCaseNo: "NO",
            cookies: cookie,
          };
          endpoint = "case/records";
          break;

        case "filing-number":
          requestData = {
            captcha: filingForm.captcha,
            case_no: filingForm.filingNumber,
            rgyear: filingForm.year,
            caseStatusSearchType: "CSfilingNumber",
            case_type: "",
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            cookies: cookie,
          };
          endpoint = "search/filing-number";
          break;

        case "advocate-name":
          requestData = {
            captcha: advocateForm.captcha,
            advocate_name: advocateForm.advocateName,
            search_type: "1",
            caseStatusSearchType: "CSAdvName",
            f: advocateForm.status,
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            cookies: cookie,
          };
          endpoint = "search/advocate-name";
          break;

        case "cnr-number":
          requestData = {
            cino: cnrForm.cino,
            captcha: cnrForm.captcha,
            caseStatusSearchType: "CNRNumber",
            appFlag: "web",
            cookies: cookie,
          };
          endpoint = "search/cnr-number";
          break;

        case "case-type":
          requestData = {
            captcha: caseTypeForm.captcha,
            case_type: caseTypeForm.caseType,
            search_year: caseTypeForm.year,
            caseStatusSearchType: "CScaseType",
            f: caseTypeForm.status,
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            cookies: cookie,
          };
          endpoint = "search/case-type";
          break;

        case "co-party-name":
          requestData = {
            captcha: coPartyForm.captcha,
            partynameOrder: coPartyForm.partyName,
            rgyearOrder: coPartyForm.registrationYear,
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            sessionId: cookie?.JSESSION || cookie?.JSESSIONID || "",
            cookies: cookie,
          };
          endpoint = "court-orders/party-name";
          break;

        case "co-case-number":
          requestData = {
            captcha: coCaseNumberForm.captcha,
            case_type_order: coCaseNumberForm.caseType,
            case_no_order: coCaseNumberForm.caseNumber,
            rgyearCaseOrder: coCaseNumberForm.registrationYear,
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            sessionId: cookie?.JSESSION || cookie?.JSESSIONID || "",
            cookies: cookie,
          };
          endpoint = "court-orders/case-number";
          break;

        case "co-order-date":
          requestData = {
            captcha: coOrderDateForm.captcha,
            from_date: toDDMMYYYY(coOrderDateForm.fromDate),
            to_date: toDDMMYYYY(coOrderDateForm.toDate),
            court_code: selectedBench,
            state_code: courtCode,
            court_complex_code: selectedBench,
            sessionId: cookie?.JSESSION || cookie?.JSESSIONID || "",
            cookies: cookie,
          };
          endpoint = "court-orders/order-date";
          break;

        default:
          return;
      }

      const data = await apiRequest(endpoint, requestData, cookie);

      // Check for error response from API
      if (data?.error) {
        const displayError = data.code === "HC_PORTAL_INVALID_RESPONSE"
          ? "No data found from public sources"
          : data.error;
        setError(displayError);
        toast.error(displayError, { duration: 5000 });
        fetchCaptcha();
        return;
      }

      // Store search payload
      localStorage.setItem("hc_search_payload", JSON.stringify(requestData));

      // CNR returns case details directly — navigate to case details page
      if (activeTab === "cnr-number") {
        const caseDetails = data?.data;
        if (!caseDetails) {
          setError("No case details found for this CNR number");
          fetchCaptcha();
          return;
        }
        navigate("/portal/caseDetails", {
          state: {
            caseDetails: caseDetails,
            courtCode: selectedBench,
            stateCode: courtCode,
            courtComplexCode: selectedBench,
            status: "pending",
            cookies: data?.cookies || cookie,
          },
        });
        return;
      }

      // Court Orders tabs return order/judgement records (different shape) —
      // route them to the dedicated Court Orders screen.
      if (searchCategory === "court-orders") {
        navigate("/portal/courtOrders", {
          state: {
            data: data,
            searchParams: requestData,
          },
        });
        return;
      }

      // All other tabs — navigate to case list
      const searchParamsWithLowercase = {
        ...requestData,
        status: (requestData.f || "pending").toLowerCase(),
      };

      navigate("/portal/caseList", {
        state: {
          data: data,
          searchParams: searchParamsWithLowercase,
        },
      });
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.message || "Failed to search cases";
      setError(errorMessage);
      toast.error(errorMessage, { duration: 5000 });
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Reset handler
  const handleReset = () => {
    const defaultYear = new Date().getFullYear().toString();
    switch (activeTab) {
      case "party-name":
        setPartyForm({ petitionerName: "", registrationYear: defaultYear, status: "Pending", captcha: "" });
        break;
      case "case-number":
        setCaseNumberForm({ caseType: "", caseNumber: "", registrationYear: defaultYear, captcha: "" });
        break;
      case "filing-number":
        setFilingForm({ filingNumber: "", year: defaultYear, captcha: "" });
        break;
      case "advocate-name":
        setAdvocateForm({ advocateName: "", status: "Pending", captcha: "" });
        break;
      case "cnr-number":
        setCnrForm({ cino: "", captcha: "" });
        break;
      case "case-type":
        setCaseTypeForm({ caseType: "", year: defaultYear, status: "Pending", captcha: "" });
        break;
      case "co-party-name":
        setCoPartyForm({ partyName: "", registrationYear: defaultYear, captcha: "" });
        break;
      case "co-case-number":
        setCoCaseNumberForm({ caseType: "", caseNumber: "", registrationYear: defaultYear, captcha: "" });
        break;
      case "co-order-date":
        setCoOrderDateForm({ fromDate: "", toDate: "", captcha: "" });
        break;
      default:
        break;
    }
    if (selectedBench) fetchCaptcha();
  };

  // Reusable status radio buttons
  const renderStatusRadio = (value, onChange, options = ["Pending", "Disposed", "Both"]) => (
    <div className="flex h-10 items-center gap-4">
      {options.map((opt) => (
        <label key={opt} className="flex cursor-pointer items-center text-sm text-slate-700">
          <input
            type="radio"
            name="statusRadio"
            value={opt}
            checked={value === opt}
            onChange={() => onChange(opt)}
            className="mr-1.5 accent-yellow-500"
          />
          {opt}
        </label>
      ))}
    </div>
  );

  const renderYearSelect = (value, onChange) => (
    <SelectShell>
      <SelectMenu chevron={false} value={value} onChange={(e) => onChange(e.target.value)} className={selectStyles} required>
        {yearOptions.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </SelectMenu>
    </SelectShell>
  );

  const renderCaseTypeSelect = (value, onChange) => (
    <SelectShell>
      <SelectMenu chevron={false} value={value} onChange={(e) => onChange(e.target.value)} className={selectStyles} required>
        <option value="" disabled>Select Case Type</option>
        {caseTypes.map((type) => (
          <option key={type.code} value={type.code}>{type.name}</option>
        ))}
      </SelectMenu>
    </SelectShell>
  );

  // Tab-specific fields — each returns grid cells laid out by the parent grid.
  const renderSearchFields = () => {
    switch (activeTab) {
      case "party-name":
        return (
          <>
            <Field label="Petitioner/Respondent Name">
              <input
                type="text"
                value={partyForm.petitionerName}
                onChange={(e) => setPartyForm({ ...partyForm, petitionerName: e.target.value })}
                placeholder="Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Registration Year">
              {renderYearSelect(partyForm.registrationYear, (val) => setPartyForm({ ...partyForm, registrationYear: val }))}
            </Field>
            <Field label="Status">
              {renderStatusRadio(partyForm.status, (val) => setPartyForm({ ...partyForm, status: val }))}
            </Field>
          </>
        );
      case "case-number":
        return (
          <>
            <Field label="Case Type">
              {renderCaseTypeSelect(caseNumberForm.caseType, (val) => setCaseNumberForm({ ...caseNumberForm, caseType: val }))}
            </Field>
            <Field label="Case Number">
              <input
                type="text"
                value={caseNumberForm.caseNumber}
                onChange={(e) => setCaseNumberForm({ ...caseNumberForm, caseNumber: e.target.value })}
                placeholder="Case Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Year">
              {renderYearSelect(caseNumberForm.registrationYear, (val) => setCaseNumberForm({ ...caseNumberForm, registrationYear: val }))}
            </Field>
          </>
        );
      case "filing-number":
        return (
          <>
            <Field label="Filing Number" className="sm:col-span-2">
              <input
                type="text"
                value={filingForm.filingNumber}
                onChange={(e) => setFilingForm({ ...filingForm, filingNumber: e.target.value })}
                placeholder="Filing Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Year">
              {renderYearSelect(filingForm.year, (val) => setFilingForm({ ...filingForm, year: val }))}
            </Field>
          </>
        );
      case "advocate-name":
        return (
          <>
            <Field label="Advocate Name" className="sm:col-span-2">
              <input
                type="text"
                value={advocateForm.advocateName}
                onChange={(e) => setAdvocateForm({ ...advocateForm, advocateName: e.target.value })}
                placeholder="Advocate Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Status">
              {renderStatusRadio(advocateForm.status, (val) => setAdvocateForm({ ...advocateForm, status: val }))}
            </Field>
          </>
        );
      case "cnr-number":
        return (
          <Field label="CNR Number" className="sm:col-span-2">
            <input
              type="text"
              value={cnrForm.cino}
              onChange={(e) => setCnrForm({ ...cnrForm, cino: e.target.value })}
              placeholder="CNR Number"
              className={inputStyles}
              required
            />
          </Field>
        );
      case "case-type":
        return (
          <>
            <Field label="Case Type">
              {renderCaseTypeSelect(caseTypeForm.caseType, (val) => setCaseTypeForm({ ...caseTypeForm, caseType: val }))}
            </Field>
            <Field label="Year">
              {renderYearSelect(caseTypeForm.year, (val) => setCaseTypeForm({ ...caseTypeForm, year: val }))}
            </Field>
            <Field label="Status">
              {renderStatusRadio(caseTypeForm.status, (val) => setCaseTypeForm({ ...caseTypeForm, status: val }), ["Pending", "Disposed"])}
            </Field>
          </>
        );
      case "co-party-name":
        return (
          <>
            <Field label="Petitioner/Respondent Name">
              <input
                type="text"
                value={coPartyForm.partyName}
                onChange={(e) => setCoPartyForm({ ...coPartyForm, partyName: e.target.value })}
                placeholder="Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Registration Year">
              {renderYearSelect(coPartyForm.registrationYear, (val) => setCoPartyForm({ ...coPartyForm, registrationYear: val }))}
            </Field>
          </>
        );
      case "co-case-number":
        return (
          <>
            <Field label="Case Type">
              {renderCaseTypeSelect(coCaseNumberForm.caseType, (val) => setCoCaseNumberForm({ ...coCaseNumberForm, caseType: val }))}
            </Field>
            <Field label="Case Number">
              <input
                type="text"
                value={coCaseNumberForm.caseNumber}
                onChange={(e) => setCoCaseNumberForm({ ...coCaseNumberForm, caseNumber: e.target.value })}
                placeholder="Case Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Year">
              {renderYearSelect(coCaseNumberForm.registrationYear, (val) => setCoCaseNumberForm({ ...coCaseNumberForm, registrationYear: val }))}
            </Field>
          </>
        );
      case "co-order-date":
        return (
          <>
            <Field label="From Date">
              <input
                type="date"
                value={coOrderDateForm.fromDate}
                onChange={(e) => setCoOrderDateForm({ ...coOrderDateForm, fromDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            <Field label="To Date">
              <input
                type="date"
                value={coOrderDateForm.toDate}
                onChange={(e) => setCoOrderDateForm({ ...coOrderDateForm, toDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
          </>
        );
      default:
        return null;
    }
  };

  // Get captcha value and setter for active tab
  const getCaptchaProps = () => {
    switch (activeTab) {
      case "party-name":
        return { value: partyForm.captcha, onChange: (e) => setPartyForm({ ...partyForm, captcha: e.target.value }) };
      case "case-number":
        return { value: caseNumberForm.captcha, onChange: (e) => setCaseNumberForm({ ...caseNumberForm, captcha: e.target.value }) };
      case "filing-number":
        return { value: filingForm.captcha, onChange: (e) => setFilingForm({ ...filingForm, captcha: e.target.value }) };
      case "advocate-name":
        return { value: advocateForm.captcha, onChange: (e) => setAdvocateForm({ ...advocateForm, captcha: e.target.value }) };
      case "cnr-number":
        return { value: cnrForm.captcha, onChange: (e) => setCnrForm({ ...cnrForm, captcha: e.target.value }) };
      case "case-type":
        return { value: caseTypeForm.captcha, onChange: (e) => setCaseTypeForm({ ...caseTypeForm, captcha: e.target.value }) };
      case "co-party-name":
        return { value: coPartyForm.captcha, onChange: (e) => setCoPartyForm({ ...coPartyForm, captcha: e.target.value }) };
      case "co-case-number":
        return { value: coCaseNumberForm.captcha, onChange: (e) => setCoCaseNumberForm({ ...coCaseNumberForm, captcha: e.target.value }) };
      case "co-order-date":
        return { value: coOrderDateForm.captcha, onChange: (e) => setCoOrderDateForm({ ...coOrderDateForm, captcha: e.target.value }) };
      default:
        return { value: "", onChange: () => {} };
    }
  };

  const currentTabs = searchCategory === "court-orders" ? COURT_ORDER_TABS : SEARCH_TABS;
  const categoryLabel = searchCategory === "court-orders" ? "Court Orders" : "Case Status";
  const activeTabLabel = currentTabs.find((t) => t.id === activeTab)?.label || "";
  const captchaProps = getCaptchaProps();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">
            Find Cases
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">High Court</span>
        </div>

        {/* Stepper */}
        <div className="mt-4">
          <FindCaseStepper current={2} />
        </div>

        {/* Form Card — full width */}
        <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <form onSubmit={handleSearch}>
            {/* Court & Bench Selection */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-b border-slate-100 p-5 sm:p-6 md:grid-cols-2">
              <Field label="High Court">
                <SelectShell>
                  <SelectMenu chevron={false} value={courtCode} onChange={handleCourtChange} className={selectStyles} required>
                    <option value="" disabled>Choose High Court</option>
                    {HIGH_COURTS.map((hc) => (
                      <option key={hc.code} value={hc.code}>{hc.name}</option>
                    ))}
                  </SelectMenu>
                </SelectShell>
              </Field>
              <Field label="Bench">
                <SelectShell busy={benchLoading}>
                  <SelectMenu chevron={false}
                    value={selectedBench}
                    onChange={handleBenchChange}
                    className={selectStyles}
                    disabled={!courtCode || benchLoading}
                    required
                  >
                    <option value="" disabled>
                      {benchLoading ? "Loading benches..." : courtCode ? "Choose Bench" : "Select a Court First"}
                    </option>
                    {benches.map((bench) => (
                      <option key={bench.code} value={bench.code}>{bench.name}</option>
                    ))}
                  </SelectMenu>
                </SelectShell>
              </Field>
            </div>

            <div className="p-5 sm:p-6">
              {/* Category — Case Status vs Court Orders */}
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 p-1">
                  {SEARCH_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleCategoryChange(cat.id)}
                      className={`rounded-full px-6 py-1.5 text-sm font-semibold transition-all duration-200 ${
                        searchCategory === cat.id
                          ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search By pills */}
              <p className="mb-2 mt-5 text-center text-xs font-medium text-slate-600">Search By</p>
              <div className="flex flex-wrap justify-center gap-2">
                {currentTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                      activeTab === tab.id
                        ? "bg-yellow-500 text-slate-900 shadow-sm"
                        : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <h2 className="mt-6 text-sm font-semibold text-slate-800">
                {categoryLabel} : Search by {activeTabLabel}
              </h2>

              {error && (
                <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">{error}</p>
              )}

              {/* Fields + captcha share one grid so they align across the full width */}
              <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
                {renderSearchFields()}

                <Field label="Captcha" className="lg:col-start-1">
                  <div className="flex items-stretch gap-2">
                    <div className="flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-slate-50">
                      {captchaLoading ? (
                        <span className="flex items-center gap-2 text-sm text-slate-500">
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Loading...
                        </span>
                      ) : captchaImage ? (
                        <img
                          src={captchaImage}
                          alt="CAPTCHA"
                          className="max-h-[34px] object-contain"
                          onError={() => setError("Failed to load captcha image.")}
                        />
                      ) : (
                        <span className="px-2 text-center text-xs text-slate-400">Select Bench to load Captcha</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={fetchCaptcha}
                      disabled={!selectedBench}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-yellow-400 hover:text-yellow-600 disabled:opacity-50 disabled:hover:border-slate-300 disabled:hover:text-slate-500"
                      title="Refresh Captcha"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                </Field>

                <Field label="Enter Captcha">
                  <input
                    type="text"
                    value={captchaProps.value}
                    onChange={captchaProps.onChange}
                    placeholder="Enter Captcha"
                    className={inputStyles}
                    required
                  />
                </Field>
              </div>
            </div>

            {/* Footer — tips + actions */}
            <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {SEARCH_TIPS.map((tip) => (
                  <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                    {tip}
                  </li>
                ))}
              </ul>
              <div className="flex shrink-0 gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="h-10 rounded-lg border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                  disabled={loading}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={loading || captchaLoading || !selectedBench}
                >
                  {loading ? "Searching..." : "Search Cases"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default Cases;
